/** @jsx jsx */
/** @jsxRuntime classic */
import { css, jsx } from "@emotion/react";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useResolvedPath, useMatch, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import ProgressBar from "react-bootstrap/ProgressBar";

import useBackendLayoutContext, { BackendLayoutContext } from "../contexts/useBackendLayoutContext";
import { useNProgress } from "@tanem/react-nprogress";
import { useAuth } from "../app/useAuth";

const NavLink = (props) => {
    const navigate = useNavigate();
    const resolved = useResolvedPath(props.to);
    const rmatched = useMatch({ path: resolved.pathname, end: props.to === "/backend" });

    const handleNavigate = useCallback(
        (e) => {
            e.preventDefault();
            navigate(props.to);
        },
        [props.to, navigate],
    );

    return (
        <Nav.Item>
            <Nav.Link href={props.to} onClick={handleNavigate} active={rmatched}>
                {props.children}
            </Nav.Link>
        </Nav.Item>
    );
};

const BackendLayoutProgress = () => {
    const context = useBackendLayoutContext();
    const { animationDuration, isFinished, progress } = useNProgress({ isAnimating: context.isAnimating });

    return (
        <div
            css={css`
                opacity: ${isFinished ? 0 : 1};
                pointer-events: none;
                transition: opacity ${animationDuration}ms linear;
            `}
        >
            <div
                className="position-fixed"
                css={css`
                    top: 0;
                    right: 0;
                    width: 100%;
                    & > .progress {
                        height: 5px;
                        border-radius: 0;
                        opacity: 0.8;
                    }
                `}
            >
                <ProgressBar now={progress * 100} />
            </div>

            <div
                css={css`
                    top: 15px;
                    right: 10px;
                    opacity: 0.125;
                `}
                className="position-fixed top-2 right-2"
            >
                {!isFinished && <Spinner animation="border" />}
            </div>
        </div>
    );
};

function Footer() {
    const getFullYearCopyright = () => {
        const d = new Date();

        if (d) {
            return d.getFullYear();
        } else {
            return "2022";
        }
    };

    return (
        <div className="navbar-light bg-light">
            <div className="container-fluid ">
                <div className="d-flex justify-content-center flex-nowrap align-items-center">
                    <div className="mx-2 p-3">Copyright © {getFullYearCopyright()}, R-DNAM ฐานข้อมูลเครื่องหมายดีเอ็นเอข้าว</div>
                </div>
            </div>
        </div>
    );
}

const BackendLayout = () => {
    const auth = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [authData, setAuthData] = useState("");

    const nav = useMemo(() => {
        return (
            <>
                {/* <NavLink to="/backend">หน้าหลัก</NavLink> */}
                {authData && authData.role === "admin" && <NavLink to="/backend/user">จัดการผู้ใช้</NavLink>}
                <NavLink to="/backend/n1">จัดการข้อมูล EST</NavLink>
                <NavLink to="/backend/n2">จัดการข้อมูล GMSV</NavLink>
                <NavLink to="/backend/n3">จัดการข้อมูล SNP Marker</NavLink>
                <NavLink to="/backend/n4">จัดการข้อมูล Primer Marker Publication</NavLink>
                <NavLink to="/backend/n5">จัดการข้อมูล Gene</NavLink>
                <NavLink to="/backend/n6">จัดการข้อมูล Primer Validation</NavLink>
                <NavLink to="/backend/n7">จัดการข้อมูล SNV</NavLink>
                <NavLink to="/backend/category">จัดการหมวดหมู่</NavLink>
                <NavLink to="/backend/tools">จัดการ link เครื่องมือ</NavLink>
            </>
        );
    }, [authData]);

    const CheckMyAccount = useCallback(() => {
        const AuthData = auth?.auth_data;

        if (AuthData?.role === "admin" || AuthData?.role === "staff") {
            setAuthData(AuthData);
            setLoading(false);
        } else {
            navigate("/");
        }
    }, [auth]);

    useEffect(() => {
        CheckMyAccount();
    }, [auth, CheckMyAccount]);

    if (loading) return <div>Loading...</div>;

    return (
        <HelmetProvider>
            <BackendLayoutContext.Provider value={{}}>
                <Navbar bg="light" expand="lg">
                    <Container fluid>
                        <Navbar.Brand href="/" onClick={(e) => (e.preventDefault(), navigate("/"))}>
                            R-DNAM ฐานข้อมูลเครื่องหมายดีเอ็นเอข้าว
                        </Navbar.Brand>
                        <Navbar.Toggle aria-controls="basic-navbar-nav" />
                        <Navbar.Collapse id="basic-navbar-nav">
                            <Nav className="d-flex d-lg-none me-auto">{nav}</Nav>
                        </Navbar.Collapse>
                    </Container>
                </Navbar>

                <div className="d-flex gap-3 w-100" style={{ minHeight: "calc(100vh - 112px)" }}>
                    <div className="d-none d-md-block p-3" style={{ flexBasis: "280px", flexGrow: 1 }}>
                        <Nav variant="pills" defaultActiveKey="/home" className="flex-column">
                            {nav}
                        </Nav>
                    </div>

                    <div className="p-3" style={{ flexBasis: 0, flexGrow: 999, minWidth: "50%" }}>
                        <Outlet />
                    </div>
                </div>

                <Footer />
            </BackendLayoutContext.Provider>
        </HelmetProvider>
    );
};

export default BackendLayout;
